import styled from "@emotion/styled";
import { captureException } from "@sentry/react";
import { useConnection } from "@solana/wallet-adapter-react";
import { PublicKey } from "@solana/web3.js";
import { convert } from "@stepfinance/common";
import { BaseButton, lightGrey2 } from "@stepfinance/react";
import { Slider, Tag } from "antd";
import type { FC, ReactNode } from "react";
import { useState } from "react";

import { getStepRewardOptionsProgram } from "../../../actions/step/reward-options/anchor";
import { exerciseOption } from "../../../actions/step/reward-options/exercise";
import { useAccountsContext, useAccountsContextRefresher } from "../../../contexts/accounts";
import { useLanguageContext } from "../../../contexts/language/language";
import { useRewards } from "../../../contexts/reward";
import type { RewardOption } from "../../../hooks";
import { useConnectedWallet } from "../../../hooks/wallet/useConnectedWallet";
import { getTokenName } from "../../../utils/tokenInfo";
import { handlePreSendTxnError } from "../../../utils/transactions";
import { formatStringWithPlaceholders, formatUSD } from "../../../utils/utils";
import { APPROVE_LABEL, AWAIT_LABEL } from "../../labels";

export interface ExerciseOptionProps {
  rewardOption: RewardOption;
  stepPrice: number | null;
  closeModal: () => void;
}

export const ExerciseOption: FC<ExerciseOptionProps> = ({ rewardOption, stepPrice, closeModal }) => {
  const { userTokenAccounts } = useAccountsContext();
  const refreshAccounts = useAccountsContextRefresher();

  const {
    currentLanguageData: {
      common: { notifications: tokenBalanceChangesNotifications },
      pages: {
        dashboard: {
          modules: {
            rewardOptions: { exerciseOptionModal: exerciseModalText, notifications: rewardOptionsNotifications, table },
          },
        },
      },
    },
  } = useLanguageContext();

  const { amount, strikePrice, rewardMint, rewardDecimals, priceDecimals, priceMint } = rewardOption;
  const rewardSymbol = getTokenName(rewardMint);
  const priceSymbol = getTokenName(priceMint);
  const userPaymentBalance = userTokenAccounts.find((b) => b.info.mint === priceMint);
  const { connection } = useConnection();
  const wallet = useConnectedWallet();
  const { refreshRewards } = useRewards();
  const [pendingTx, setPendingTx] = useState(false);
  const [pendingConfirmation, setPendingConfirmation] = useState(false);
  const [exercisePercent, setExercisePercent] = useState(100);
  const rewardAmount = convert(amount, rewardDecimals);
  const exercisePrice = convert(strikePrice, priceDecimals);
  const displayAmount = rewardAmount * (exercisePercent / 100);
  const exerciseAmount = Math.ceil((amount * exercisePercent) / 100);
  const exerciseTotal = exercisePrice * displayAmount;
  const exerciseTotalDisplay = formatUSD.format(exerciseTotal);
  const insufficientUSDCBalance = (userPaymentBalance?.tokenAmount.uiAmount ?? 0) < exerciseTotal;

  let buttonText: string | ReactNode[] = `Buy ${
    displayAmount > 0 ? displayAmount.toFixed(rewardDecimals) : 0
  } ${rewardSymbol} for ${exerciseTotalDisplay} ${priceSymbol}`;
  if (!displayAmount) {
    buttonText = exerciseModalText.buttonText.setAnAmount;
  } else if (insufficientUSDCBalance) {
    buttonText = formatStringWithPlaceholders(exerciseModalText.buttonText.insufficientTokenFunds, priceSymbol);
  } else if (pendingTx) {
    buttonText = APPROVE_LABEL;
  } else if (pendingConfirmation) {
    buttonText = AWAIT_LABEL;
  }
  const percentSavings = stepPrice ? ((stepPrice - exercisePrice) / stepPrice) * 100 : 0;

  return (
    <ExerciseContainer>
      <span className="exercise label">{exerciseModalText.exerciseAmount}</span>
      <span className="percent">{exercisePercent.toFixed(2)}%</span>
      <ExerciseSlider value={exercisePercent} step={0.01} onChange={setExercisePercent} tooltipVisible={false} />
      <div className="percentage-buttons">
        <ExercisePercentageTag percentage={25} onClick={setExercisePercent} />
        <ExercisePercentageTag percentage={50} onClick={setExercisePercent} />
        <ExercisePercentageTag percentage={75} onClick={setExercisePercent} />
        <ExercisePercentageTag percentage={100} onClick={setExercisePercent} />
      </div>
      <StatRow>
        <span className="label">{exerciseModalText.youAreBuying}</span>
        <span className="value">
          {displayAmount > 0 ? displayAmount.toFixed(9) : 0} {rewardSymbol}
        </span>
      </StatRow>
      <StatRow>
        <span className="label">{exerciseModalText.atAFixedPriceOf}</span>
        <span className="price">
          <span className="value">${exercisePrice.toFixed(6)}</span>
          <span className="label">
            {formatStringWithPlaceholders(
              exerciseModalText.percentSavings,
              percentSavings.toFixed(2),
              stepPrice ? formatUSD.format(stepPrice) : table.missingPrice,
            )}
          </span>
        </span>
      </StatRow>

      <StatRow>
        <span className="label">{exerciseModalText.forATotalOf}</span>
        <span className="value">
          {exerciseTotalDisplay} {priceSymbol}
        </span>
      </StatRow>
      <BuyButton
        className="buy"
        disabled={insufficientUSDCBalance || !displayAmount || pendingTx || pendingConfirmation}
        onClick={async () => {
          if (wallet && userPaymentBalance) {
            const program = getStepRewardOptionsProgram(connection, wallet);
            try {
              setPendingTx(true);
              await exerciseOption(
                connection,
                program,
                rewardOption,
                wallet,
                new PublicKey(userPaymentBalance.pubkey),
                exerciseAmount,
                exerciseTotal,
                () => setPendingConfirmation(true),
                { ...rewardOptionsNotifications, ...tokenBalanceChangesNotifications },
              );
            } catch (err) {
              captureException(err);
              handlePreSendTxnError(err as Error, rewardOptionsNotifications.errorClaimingOption.message);
            } finally {
              await refreshRewards();
              refreshAccounts();
              setPendingTx(false);
              setPendingConfirmation(false);
              closeModal();
            }
          }
        }}
      >
        {buttonText}
      </BuyButton>
      <span className="help-text">{exerciseModalText.remainingAmountBlurb}</span>
    </ExerciseContainer>
  );
};

const ExerciseContainer = styled.div`
  display: flex;
  flex-direction: column;

  .percentage-buttons {
    display: flex;
    justify-content: flex-end;
    padding-bottom: 30px;
    padding-top: 10px;
  }
  .label {
    text-align: left;
    font-weight: 400;
    color: ${lightGrey2};
  }
  .exercise {
    padding-bottom: 10px;
  }
  .percent {
    font-size: 30px;
    font-weight: 700;
    line-height: 22px;
    text-align: left;
  }
  .price {
    display: flex;
    flex-direction: column;
    .label {
      font-size: 10px;
    }
  }

  .help-text {
    text-align: center;
    padding-top: 5px;
    color: ${lightGrey2};
    font-size: 10px;
  }
  .right {
    text-align: right;
    padding: 10px;
  }
`;

const BuyButton = styled(BaseButton)`
  width: 100%;
  padding: 10px;
  font-size: 16px;
  line-height: 22px;
  font-weight: 800;
  height: 60px;
  letter-spacing: 0em;
`;
const StatRow = styled.div`
  letter-spacing: 0em;
  line-height: 22px;
  font-size: 14px;
  display: flex;
  justify-content: space-between;
  padding-bottom: 20px;

  .value {
    font-weight: 700;
    text-align: right;
  }
`;

const ExerciseSlider = styled(Slider)`
  margin-top: 20px;
  .ant-slider-rail {
    background-color: #004735;
  }

  :hover .ant-slider-rail {
    background-color: #004735;
  }

  .ant-slider-handle:not(.ant-tooltip-open) {
    border-color: #06d6a0;
  }
`;
type ExercisePercentageTagProps = {
  percentage: number;
  className?: string;
  onClick?: (percentage: number) => void;
};

const BaseExercisePercentageTag: FC<ExercisePercentageTagProps> = ({ percentage, className, onClick }) => (
  <PercentTag className={className} onClick={() => onClick?.(percentage)}>
    <span>{percentage}%</span>
  </PercentTag>
);

const ExercisePercentageTag = styled(BaseExercisePercentageTag)`
  margin-left: 60px;
`;

const PercentTag = styled(Tag)`
  display: flex;
  flex-direction: column;
  justify-content: center;
  background: #282828;
  border: 1px solid #434343;
  box-sizing: border-box;
  border-radius: 2px;
  padding: 1px 10px;
  text-align: center;
  cursor: pointer;
  user-select: none;
  margin-right: 0;
  height: 22px;
  width: 52px;

  span {
    font-weight: bold;
    font-size: 10px;
    line-height: 20px;
  }
`;
